import React, { useState } from 'react';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LinkSvg } from '../../../assets/images/ui/link.svg';

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 16,
    height: 16,
    flex: '0 0 16px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': { width: 16, height: 16, display: 'block' },
    '& path': { stroke: 'currentColor' }
}));

const Root = styled(Link, {
    shouldForwardProp: (prop) => !['basecolor', 'hovercolor', 'disableicon', 'withunderline'].includes(prop)
})(({ theme, basecolor = '#0F56D6', hovercolor = '#054CCC', disableicon, withunderline = true }) => ({
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
    gap: 4,
    maxWidth: '100%',
    borderBottom: withunderline ? `1px solid ${basecolor}` : 'none',
    color: basecolor,
    textDecoration: 'none',
    fontFamily: 'Roboto, sans-serif',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: 16,
    lineHeight: '140%',
    '&:hover': {
        color: hovercolor,
        ...(withunderline ? { borderBottomColor: hovercolor } : {}),
    },
    '&:active': {
        color: hovercolor,
        ...(withunderline ? { borderBottomColor: hovercolor } : {}),
    },
}));

export default function BlueLink({ href, children, sx, icon = true, withUnderline = true, copyOnClick = false, target, rel, baseColor = '#0F56D6', hoverColor = '#054CCC', ...props }) {
    const { t } = useTranslation();
    const linkRel = rel || (target === '_blank' ? 'noopener noreferrer' : undefined);
    const [copied, setCopied] = useState(false);
    
    const handleCopy = () => {
        if (href) {
            navigator.clipboard.writeText(href);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };
    
    const handleIconClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        handleCopy();
    };
    
    const handleLinkClick = (e) => {
        if (copyOnClick) {
            e.preventDefault();
            handleCopy();
        }
    };
    
    return (
        <Root
            href={copyOnClick ? undefined : href}
            sx={{ ...sx, ...(copyOnClick ? { cursor: 'pointer' } : {}) }}
            basecolor={baseColor}
            hovercolor={hoverColor}
            withunderline={withUnderline}
            underline="none"
            target={copyOnClick ? undefined : target}
            rel={copyOnClick ? undefined : linkRel}
            onClick={handleLinkClick}
            {...props}
        >
            {icon && (
                <Tooltip 
                    title={t('Скопировано')} 
                    open={copied} 
                    disableHoverListener 
                    disableFocusListener 
                    disableTouchListener
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#CEF7E7',
                                color: '#545A71',
                                fontFamily: 'Roboto',
                                fontWeight: 400,
                                fontSize: '14px',
                                lineHeight: '130%',
                                borderRadius: '16px',
                                px: 1.5,
                                py: 1.5,
                            },
                        },
                        arrow: {
                            sx: {
                                color: '#CEF7E7',
                            },
                        },
                    }}
                >
                    <IconWrapper aria-hidden onClick={handleIconClick} sx={{ cursor: 'pointer' }}>
                        <LinkSvg />
                    </IconWrapper>
                </Tooltip>
            )}
            <Box component="span" sx={{ overflow: 'inherit', textOverflow: 'inherit', whiteSpace: 'inherit', minWidth: 0 }}>
                {children}
            </Box>
        </Root>
    );
}
